
import { fetchAuditLogs } from './auditLogService';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';

/**
 * Compliance Report Service
 * Generates compliance reports with CSV/PDF export capabilities
 */

/**
 * Fetch compliance report data with filters
 * @param {Object} filters - Filter options
 * @returns {Object} Report data with logs and statistics
 */
export const fetchComplianceReportData = async (filters = {}) => {
  try {
    // Fetch audit logs with filters
    const { data: logs, error } = await fetchAuditLogs(filters);

    if (error) {
      console.error('Error fetching compliance report data:', error);
      return { data: null, error };
    }

    // Calculate statistics
    const stats = calculateReportStatistics(logs);

    return {
      data: {
        logs: logs || [],
        statistics: stats,
        filters: filters,
        generatedAt: new Date()?.toISOString()
      },
      error: null
    };
  } catch (error) {
    console.error('Exception in fetchComplianceReportData:', error);
    return { data: null, error };
  }
};

/**
 * Calculate report statistics
 * @param {Array} logs - Audit logs
 * @returns {Object} Statistics
 */
const calculateReportStatistics = (logs) => {
  const stats = {
    totalEvents: logs?.length || 0,
    bySeverity: {
      critical: 0,
      warning: 0,
      info: 0
    },
    byCategory: {
      security: 0,
      compliance: 0,
      data_modification: 0,
      system: 0
    },
    byEventType: {},
    uniqueUsers: new Set(),
    uniqueDealerships: new Set(),
    dateRange: {
      earliest: null,
      latest: null
    }
  };

  logs?.forEach(log => {
    // Count by severity
    if (log?.severity) {
      stats.bySeverity[log.severity] = (stats?.bySeverity?.[log?.severity] || 0) + 1;
    }

    // Count by category
    if (log?.eventCategory) {
      stats.byCategory[log.eventCategory] = (stats?.byCategory?.[log?.eventCategory] || 0) + 1;
    }

    // Count by event type
    if (log?.eventType) {
      stats.byEventType[log.eventType] = (stats?.byEventType?.[log?.eventType] || 0) + 1;
    }

    // Track unique users
    if (log?.userEmail) {
      stats?.uniqueUsers?.add(log?.userEmail);
    }

    // Track unique dealerships
    if (log?.dealershipId) {
      stats?.uniqueDealerships?.add(log?.dealershipId);
    }

    // Track date range
    if (log?.createdAt) {
      const logDate = new Date(log.createdAt);
      if (!stats?.dateRange?.earliest || logDate < new Date(stats.dateRange.earliest)) {
        stats.dateRange.earliest = log?.createdAt;
      }
      if (!stats?.dateRange?.latest || logDate > new Date(stats.dateRange.latest)) {
        stats.dateRange.latest = log?.createdAt;
      }
    }
  });

  // Convert Sets to counts
  stats.uniqueUsers = stats?.uniqueUsers?.size;
  stats.uniqueDealerships = stats?.uniqueDealerships?.size;

  return stats;
};

/**
 * Export compliance report to CSV
 * @param {Array} logs - Audit logs
 * @param {Object} filters - Applied filters
 * @returns {string} CSV content
 */
export const exportToCSV = (logs, filters = {}) => {
  try {
    // Prepare data for CSV
    const csvData = logs?.map(log => ({
      'Datum/Zeit': format(new Date(log?.createdAt), 'dd.MM.yyyy HH:mm:ss'),
      'Ereignistyp': log?.eventType || '',
      'Kategorie': log?.eventCategory || '',
      'Schweregrad': log?.severity || '',
      'Benutzer': log?.userEmail || 'System',
      'Entitätstyp': log?.entityType || '',
      'Entitätsname': log?.entityName || '',
      'Beschreibung': log?.actionDescription || '',
      'Partner': log?.dealershipName || '',
      'Antrags-ID': log?.applicationId || '',
      'Kunde': log?.applicationCustomerName || ''
    }));

    // Convert to CSV
    const csv = Papa?.unparse(csvData, {
      quotes: true,
      delimiter: ',',
      header: true
    });

    return csv;
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw error;
  }
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - File name
 */
export const downloadCSV = (csvContent, filename = 'compliance-report.csv') => {
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link?.setAttribute('href', url);
    link?.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw error;
  }
};

/**
 * Export compliance report to PDF
 * @param {Array} logs - Audit logs
 * @param {Object} statistics - Report statistics
 * @param {Object} filters - Applied filters
 * @returns {jsPDF} PDF document
 */
export const exportToPDF = (logs, statistics, filters = {}) => {
  try {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    const pageWidth = doc?.internal?.pageSize?.getWidth();
    const pageHeight = doc?.internal?.pageSize?.getHeight();
    
    // Header
    doc?.setFontSize(18);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Compliance-Bericht', pageWidth / 2, 15, { align: 'center' });
    
    // Report metadata
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    const generatedDate = format(new Date(), 'dd.MM.yyyy HH:mm:ss');
    doc?.text(`Erstellt am: ${generatedDate}`, 14, 25);
    
    // Date range filter
    if (filters?.dateFrom || filters?.dateTo) {
      const dateRangeText = `Zeitraum: ${filters?.dateFrom ? format(new Date(filters.dateFrom), 'dd.MM.yyyy') : 'Anfang'} - ${filters?.dateTo ? format(new Date(filters.dateTo), 'dd.MM.yyyy') : 'Ende'}`;
      doc?.text(dateRangeText, 14, 30);
    }
    
    // Statistics summary
    let yPos = filters?.dateFrom || filters?.dateTo ? 38 : 33;
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.text('Zusammenfassung', 14, yPos);
    
    yPos += 7;
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.text(`Gesamt-Ereignisse: ${statistics?.totalEvents || 0}`, 14, yPos);
    doc?.text(`Kritisch: ${statistics?.bySeverity?.critical || 0}`, 70, yPos);
    doc?.text(`Warnungen: ${statistics?.bySeverity?.warning || 0}`, 110, yPos);
    doc?.text(`Info: ${statistics?.bySeverity?.info || 0}`, 150, yPos);
    doc?.text(`Benutzer: ${statistics?.uniqueUsers || 0}`, 180, yPos);
    doc?.text(`Partner: ${statistics?.uniqueDealerships || 0}`, 220, yPos);
    
    // Table data
    yPos += 10;
    const tableData = logs?.map(log => [
      format(new Date(log?.createdAt), 'dd.MM.yyyy HH:mm'),
      log?.eventType || '',
      log?.eventCategory || '',
      log?.severity || '',
      log?.userEmail || 'System',
      log?.actionDescription?.substring(0, 50) + (log?.actionDescription?.length > 50 ? '...' : '') || ''
    ]);
    
    doc?.autoTable({
      head: [['Datum/Zeit', 'Ereignistyp', 'Kategorie', 'Schweregrad', 'Benutzer', 'Beschreibung']],
      body: tableData,
      startY: yPos,
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 14, right: 14 },
      didDrawPage: (data) => {
        // Footer
        doc?.setFontSize(8);
        doc?.setTextColor(128);
        doc?.text(
          `Seite ${doc?.internal?.getCurrentPageInfo()?.pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
    });
    
    return doc;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw error;
  }
};

/**
 * Download PDF file
 * @param {jsPDF} pdfDoc - PDF document
 * @param {string} filename - File name
 */
export const downloadPDF = (pdfDoc, filename = 'compliance-report.pdf') => {
  try {
    pdfDoc?.save(filename);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};

/**
 * Get available event types for filtering
 * @returns {Array} Event types
 */
export const getAvailableEventTypes = () => {
  return [
    { value: 'application_created', label: 'Antrag erstellt' },
    { value: 'application_status_changed', label: 'Antragsstatus geändert' },
    { value: 'application_updated', label: 'Antrag aktualisiert' },
    { value: 'application_deleted', label: 'Antrag gelöscht' },
    { value: 'dealership_created', label: 'Partner erstellt' },
    { value: 'dealership_updated', label: 'Partner aktualisiert' },
    { value: 'dealership_deleted', label: 'Partner gelöscht' },
    { value: 'dealership_status_changed', label: 'Partnerstatus geändert' },
    { value: 'voucher_created', label: 'Gutschein erstellt' },
    { value: 'voucher_redeemed', label: 'Gutschein eingelöst' },
    { value: 'voucher_verified', label: 'Gutschein verifiziert' },
    { value: 'email_sent', label: 'E-Mail gesendet' },
    { value: 'document_uploaded', label: 'Dokument hochgeladen' },
    { value: 'document_viewed', label: 'Dokument angesehen' },
    { value: 'bulk_action_executed', label: 'Massenaktion ausgeführt' },
    { value: 'login_success', label: 'Login erfolgreich' },
    { value: 'login_failed', label: 'Login fehlgeschlagen' },
    { value: 'logout', label: 'Logout' },
    { value: 'settings_changed', label: 'Einstellungen geändert' },
    { value: 'template_modified', label: 'Vorlage geändert' }
  ];
};

/**
 * Get available event categories for filtering
 * @returns {Array} Event categories
 */
export const getAvailableEventCategories = () => {
  return [
    { value: 'security', label: 'Sicherheit' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'data_modification', label: 'Datenänderung' },
    { value: 'system', label: 'System' }
  ];
};

/**
 * Get available severity levels for filtering
 * @returns {Array} Severity levels
 */
export const getAvailableSeverityLevels = () => {
  return [
    { value: 'info', label: 'Info' },
    { value: 'warning', label: 'Warnung' },
    { value: 'critical', label: 'Kritisch' }
  ];
};